import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { BASE_URL } from "../utils/helpers";

interface UseAuthenticatedGetOptions<TData>
  extends Omit<UseQueryOptions<TData, Error>, "queryKey" | "queryFn"> {
  tokenKey?: string;
  params?: Record<string, string | number | boolean | undefined | null>;
  queryKey?: unknown[];
}

export function useGetAuth<TData = unknown>(
  endpoint: string,
  options?: UseAuthenticatedGetOptions<TData>
): UseQueryResult<TData, Error> {
  const {
    tokenKey = "_token",
    params,
    queryKey: providedQueryKey,
    ...queryOptions
  } = options || {};

  let url = `${BASE_URL}/${endpoint.replace(/^\//, "")}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) url += `?${queryString}`;
  }

  const queryKey = providedQueryKey ?? [endpoint];

  return useQuery<TData, Error>({
    queryKey,
    queryFn: async (): Promise<TData> => {
      const token = localStorage.getItem(tokenKey);
      if (!token) {
        throw new Error(`No token found in localStorage (${tokenKey})`);
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem(tokenKey);
          window.location.href = "/login";
          throw new Error("Unauthorized");
        }
        const errText = await response.text();
        throw new Error(
          `HTTP ${response.status}: ${errText || response.statusText}`
        );
      }

      return response.json();
    },
    ...queryOptions,
  });
}
